import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { FxAlert, FxIcon, FxUploadDialog } from '@/components/fx'
import { PlanQuota, limitMessage } from '@/components/PlanQuota'
import {
  DEFAULT_MAX_UPLOAD_MB,
  UPLOAD_ACCEPT,
  UPLOAD_EXTENSIONS,
  UPLOAD_FORMATS_LABEL,
} from '@/components/upload/uploadQueue'
import { asLimitRefusal, templatesApi } from '@/lib/api'
import { DEFAULT_THRESHOLD, readDefaultThreshold } from '@/pages/einstellungen/defaultThreshold'
import type { DetectionMethod } from '@/types/domain'

/**
 * "Neues Musterformular" — the shell's "+", as a dialog over whatever screen
 * is open.
 *
 * ## Why a dialog in the shell, and not the funnel
 *
 * The "+" sits in the rail and in the bottom nav, so it exists on all six
 * shell routes. It used to set `?dialog=upload` and leave the screen to
 * answer; only the Übersicht ever did, by navigating to `/onboarding` — the
 * pre-account funnel: a four-step sign-up wizard that says "du", asks for a
 * **filled** form and stashes it in IndexedDB so a visitor can try the
 * product before having an account. None of that is what a signed-in person
 * pressing "+" is doing: they have an account, they are creating a template
 * from a **blank** form, and there is no wizard to walk. Mounting the dialog
 * here makes the button work identically on every route with no per-screen
 * wiring, which is exactly what the artboards draw — `Main.dc.html` points
 * both the rail's "+" and the Übersicht's create tile at one upload overlay.
 *
 * ## The query parameter is `?neu=musterformular`, not `?dialog=upload`
 *
 * Inside the cockpit `?dialog=upload` means "Dokumente hinzufügen" — scans
 * for a template that already exists (`UploadDialog.dc.html`). That is a
 * different action, and the shell's "+" is drawn on cockpit routes too, so
 * sharing the parameter would open the wrong dialog there. A parameter of
 * its own also keeps the dialog linkable and survives a reload.
 */

/** `?neu=musterformular` as a boolean, plus the two ways to flip it. */
export function useCreateTemplateDialog() {
  const [params, setParams] = useSearchParams()

  const open = useCallback(() => {
    const next = new URLSearchParams(params)
    next.set('neu', 'musterformular')
    setParams(next)
  }, [params, setParams])

  // `replace`, so Escape leaves the screen the person was on rather than
  // stepping the back button through a dialog that is already shut.
  const close = useCallback(() => {
    const next = new URLSearchParams(params)
    next.delete('neu')
    setParams(next, { replace: true })
  }, [params, setParams])

  return { isOpen: params.get('neu') === 'musterformular', open, close }
}

/**
 * Geometric detection, not the model — the same call `PickFormPage` makes,
 * for one of its two reasons: `cv` needs neither an API key nor a consent
 * record, so it cannot fail closed. A deployment with `LLM_PASS_ENABLED` off
 * would otherwise answer with a template that has no boxes at all. (The
 * other reason, that a filled form must not be sent to a vendor, does not
 * apply here — this one is blank — but the editor's "Felder erkennen" offers
 * the model per page for exactly that improvement.)
 */
// The model detector: it reads a field with no printed border, which the
// geometric one cannot see. The gateway falls back to the geometric detector
// by itself if the model run errors or finds nothing, so this cannot leave
// somebody with an empty template.
const DETECT_METHOD: DetectionMethod = 'llm'

const EXTENSIONS = UPLOAD_EXTENSIONS.map((extension) => `.${extension}`)

/** Why this file cannot be used, or null when it can. "Sie", like the app. */
export function templateFileRefusal(file: File, maxMb = DEFAULT_MAX_UPLOAD_MB): string | null {
  const name = file.name.toLowerCase()
  if (!EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return 'Wir können PDF, JPG und PNG lesen — diese Datei leider nicht.'
  }
  if (file.size > maxMb * 1024 * 1024) {
    return `Die Datei ist größer als ${maxMb} MB. Ein Foto der Seite reicht völlig.`
  }
  return null
}

/**
 * Mounted once by `AppShell`. Renders nothing until the parameter is set, so
 * the form's state — the chosen file, the progress, an error — is created
 * and discarded with the dialog rather than living for the session.
 */
export function CreateTemplateDialog() {
  const { isOpen, close } = useCreateTemplateDialog()
  if (!isOpen) return null
  return <CreateTemplateForm onClose={close} />
}

function CreateTemplateForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [sent, setSent] = useState(0)

  const upload = useMutation({
    mutationFn: (chosen: File) =>
      templatesApi.upload({
        file: chosen,
        filename: chosen.name,
        detectMethod: DETECT_METHOD,
        onProgress: (loaded, total) => setSent(total > 0 ? loaded / total : 0),
      }),
    onSuccess: async (result) => {
      // Einstellungen → Auswertung promises "Der Wert gilt für Formulare, die
      // Sie ab jetzt in diesem Browser anlegen". The funnel's AnalysePage
      // keeps that promise; this is the other way a form gets created, so it
      // has to keep it too or the sentence is false for every form made from
      // the "+". A failure is not worth failing the creation for: the form
      // exists at the gateway's own default and its settings tab can change
      // it.
      const preferred = readDefaultThreshold()
      if (preferred !== DEFAULT_THRESHOLD) {
        try {
          await templatesApi.setConfidenceThreshold(result.id, preferred)
        } catch {
          /* keep the gateway's default rather than lose the new form */
        }
      }
      // The Übersicht's list and every card's counts are keyed on
      // `['templates']`; without this the new form appears up to 30 seconds
      // later, when the poll comes round.
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
      // No `close()` first: the navigation leaves this route and takes the
      // parameter with it, and setting search params on a screen that is
      // being replaced logs a React warning for nothing.
      navigate(`/templates/${result.id}/felder-bestaetigen`, {
        state: { detection: result.detection },
      })
    },
  })

  const busy = upload.isPending
  const limit = asLimitRefusal(upload.error)
  // The detector runs before the gateway answers, so the bar reaching 100 %
  // is not the end of the wait — it is where the waiting changes character.
  const transferred = sent >= 1

  function choose(files: FileList) {
    const chosen = files[0]
    if (!chosen) return
    const why = templateFileRefusal(chosen)
    setRefusal(why)
    setFile(why ? null : chosen)
    upload.reset()
    setSent(0)
  }

  return (
    <FxUploadDialog
      title="Neues Musterformular"
      subtitle={
        <>
          Laden Sie ein <b>leeres</b> Exemplar Ihres Formulars hoch. Daraus lernt Formalogix,
          welche Felder es in Ihren ausgefüllten Dokumenten lesen soll.
        </>
      }
      accept={UPLOAD_ACCEPT}
      formats={UPLOAD_FORMATS_LABEL}
      maxSizeLabel={`Maximalgröße: ${DEFAULT_MAX_UPLOAD_MB} MB`}
      ready={file != null && !busy}
      submitLabel={busy ? 'Felder werden erkannt …' : 'Hochladen und Felder erkennen'}
      onFilesSelected={choose}
      onSubmit={() => file && upload.mutate(file)}
      // Closable at any time, including mid-upload: a close button that
      // silently does nothing is the defect this dialog exists to fix. The
      // request keeps running and the form is created either way — what a
      // person who closes early gives up is the confirmation screen, and the
      // form is in the list when the next refetch comes round.
      onClose={onClose}
    >
      {/* The template limit is what a free account hits first, and it refuses
          the upload — better said before the file is picked than after. */}
      <PlanQuota kind="templates" />

      {refusal && <FxAlert tone="danger">{refusal}</FxAlert>}

      {file && !refusal && (
        <div className="flex items-center gap-3 text-body">
          <FxIcon
            name={busy ? 'upload' : 'check_circle'}
            size={22}
            className={busy ? 'flex-none text-primary-text' : 'flex-none text-success-text'}
          />
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          {busy && (
            <span className="text-small text-text-muted">
              {transferred ? 'Felder werden erkannt …' : `${Math.round(sent * 100)} %`}
            </span>
          )}
        </div>
      )}

      {busy && (
        <p role="status" className="m-0 text-small text-text-muted">
          {transferred
            ? 'Die Datei ist da. Wir suchen jetzt die Felder — das dauert einen Moment.'
            : 'Die Datei wird übertragen …'}
        </p>
      )}

      {limit && (
        <FxAlert tone="warning" title={limitMessage(limit).title}>
          {limitMessage(limit).body}
        </FxAlert>
      )}

      {upload.isError && !limit && (
        <FxAlert tone="danger" title="Das Formular konnte nicht angelegt werden">
          Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.
        </FxAlert>
      )}
    </FxUploadDialog>
  )
}
